/**
 * 電子書籍変換用の型定義
 * BE側 (media-processing-service/api/routes/ebook.py) のPydanticモデルと対応
 */

// 入力フォーマット（エピソードのmime_typeから判定）
export type EbookInputFormat = 'pdf' | 'epub' | 'azw3' | 'azw'

// 出力フォーマット（PDF/EPUBのみサポート）
export type EbookOutputFormat = 'pdf' | 'epub'

// MIMEタイプからEbookInputFormatへのマッピング
export const MIME_TO_INPUT_FORMAT: Record<string, EbookInputFormat> = {
  'application/pdf': 'pdf',
  'application/epub+zip': 'epub',
  'application/vnd.amazon.ebook': 'azw3',
  'application/x-mobipocket-ebook': 'azw',
}

// 変換可能なMIMEタイプのセット
export const CONVERTIBLE_MIME_TYPES = new Set(Object.keys(MIME_TO_INPUT_FORMAT))

// ブラウザで直接読めないフォーマット（ドロワーを開いて変換を促す）
export const NON_READABLE_MIME_TYPES = new Set([
  'application/vnd.amazon.ebook', // AZW3
  'application/x-mobipocket-ebook', // MOBI/AZW
])

// EpisodeDrawer に表示しないフォーマット（内部利用データ）
export const HIDDEN_MIME_TYPES = new Set([
  'application/json', // 文字起こしJSONなど
  'application/x-cue', // CUEシート
])

/**
 * 入力フォーマットから利用可能な出力フォーマットを取得
 * - PDF → EPUB のみ
 * - EPUB → PDF のみ
 * - AZW3 → PDF, EPUB
 */
export function getAvailableOutputFormats(inputFormat: EbookInputFormat): EbookOutputFormat[] {
  switch (inputFormat) {
    case 'pdf':
      return ['epub']
    case 'epub':
      return ['pdf']
    case 'azw3':
    case 'azw':
      return ['pdf', 'epub']
    default:
      return []
  }
}

/**
 * MIMEタイプからEbookInputFormatを取得
 */
export function getInputFormatFromMime(mimeType: string): EbookInputFormat | null {
  return MIME_TO_INPUT_FORMAT[mimeType] ?? null
}

/**
 * 変換可能なフォーマットかどうかを判定
 */
export function isConvertibleFormat(mimeType: string): boolean {
  return CONVERTIBLE_MIME_TYPES.has(mimeType)
}

// ============================================================
// 変換オプション型定義
// ============================================================

/**
 * PDF変換オプション
 */
export type PDFOptions = {
  /** 用紙サイズ (a4, letter, legal, cover等) */
  paper_size?: PaperSizeValue
  /** カスタムサイズ (WIDTHxHEIGHT in points) */
  custom_size?: string
  /** ページ番号を追加 */
  page_numbers?: boolean
  /** デフォルトフォントサイズ (6-72) */
  default_font_size?: number
  /** 等幅フォントサイズ (6-72) */
  mono_font_size?: number
  /** セリフフォントファミリー */
  serif_family?: string
  /** サンセリフフォントファミリー */
  sans_family?: string
  /** 上マージン (ポイント) */
  margin_top?: number
  /** 下マージン (ポイント) */
  margin_bottom?: number
  /** 左マージン (ポイント) */
  margin_left?: number
  /** 右マージン (ポイント) */
  margin_right?: number
}

/**
 * EPUB変換オプション
 */
export type EPUBOptions = {
  /** デフォルトカバーを使用しない */
  no_default_cover?: boolean
  /** カバーのアスペクト比を保持 */
  preserve_cover_aspect_ratio?: boolean
  /** EPUBバージョン ("2" | "3") */
  epub_version?: '2' | '3'
  /** フローサイズ (KB) */
  flow_size?: number
  /** SVGカバーを使用しない */
  no_svg_cover?: boolean
  /** 目次のタイトル */
  toc_title?: string
}

/**
 * 変換オプション統合モデル
 */
export type ConversionOptions = {
  pdf?: PDFOptions
  epub?: EPUBOptions
}

// ============================================================
// デフォルト値
// ============================================================

export const DEFAULT_PDF_OPTIONS: PDFOptions = {}

export const DEFAULT_EPUB_OPTIONS: EPUBOptions = {}

// ============================================================
// SSEイベント型
// ============================================================

export type ConversionStatus = 'starting' | 'processing' | 'completed' | 'error'

export type ConversionProgressEvent = {
  status: ConversionStatus
  progress?: number
  message: string
  download_url?: string
}

// ============================================================
// 用紙サイズオプション
// ============================================================

export const PAPER_SIZE_OPTIONS = [
  { value: 'none', label: '指定しない' },
  { value: 'cover', label: 'サムネイルに合わせる' },
  { value: 'custom', label: 'カスタムサイズ' },
  { value: 'a0', label: 'A0 (841 x 1189 mm)' },
  { value: 'a1', label: 'A1 (594 x 841 mm)' },
  { value: 'a2', label: 'A2 (420 x 594 mm)' },
  { value: 'a3', label: 'A3 (297 x 420 mm)' },
  { value: 'a4', label: 'A4 (210 x 297 mm)' },
  { value: 'a5', label: 'A5 (148 x 210 mm)' },
  { value: 'a6', label: 'A6 (105 x 148 mm)' },
  { value: 'b0', label: 'B0 (1000 x 1414 mm)' },
  { value: 'b1', label: 'B1 (707 x 1000 mm)' },
  { value: 'b2', label: 'B2 (500 x 707 mm)' },
  { value: 'b3', label: 'B3 (353 x 500 mm)' },
  { value: 'b4', label: 'B4 (250 x 353 mm)' },
  { value: 'b5', label: 'B5 (176 x 250 mm)' },
  { value: 'b6', label: 'B6 (125 x 176 mm)' },
  { value: 'legal', label: 'Legal (8.5 x 14 inch)' },
  { value: 'letter', label: 'Letter / ANSI A (8.5 x 11 inch)' },
] as const

/** 用紙サイズの有効値型（PAPER_SIZE_OPTIONS から自動導出） */
export type PaperSizeValue = (typeof PAPER_SIZE_OPTIONS)[number]['value']
